import { defineComponent as _defineComponent } from 'vue'
import { ObjectType } from '@/@types/graphql-types';
import type { SponsoredAdFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import { type SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { ref, computed, watch, provide, onMounted } from 'vue';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import {
	GetBuyBoxOffersDocument,
	GetBuyBoxOffersQueryVariables,
	type GetBuyBoxOffersQuery,
} from '@/components/buybox-new/graphql/GetBuyBoxOffers.query';
import { timestamp } from '@/helpers/date-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { BuyBoxFilterOptions, NewBuyBoxView } from '@/stores/user.store';
import { ModalHelper } from '@/helpers/modal-helper';
import RegularBuyBoxNew from '@/components/buybox-new/RegularBuyBoxNew.vue';
import BuyBoxCountrySwitcher from '@/components/buybox-new/BuyBoxCountrySwitcher.vue';
import { useQuery } from '@/helpers/composables/useApollo';
import { useI18n } from '@/helpers/composables/useI18n';
import { useLanguageStore, useUserStore, useConstantStore } from '@/helpers/composables/useStores';
import { useTitleWatchlist } from '@/helpers/composables/useTitleWatchlist';

// need to import this immediately because it's above the fold and could cause CLS
import PromotedBuyBoxNew from '@/components/buybox-new/PromotedBuyBoxNew.vue';
import BuyBoxFilterNew from '@/components/buybox-new/BuyBoxFilterNew.vue';
import BuyBoxTypeSwitcher from '@/components/buybox-new/BuyBoxTypeSwitcher.vue';
import BuyBoxUpcomingWithNoOffers from '@/components/buybox-new/BuyBoxUpcomingWithNoOffers.vue';
import { buyboxFilterByMonetizationType } from './new-buybox-config';
import { BuyBoxOffers, useBuyBoxData } from './composables/useBuyboxData';
import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';

import { until, useMounted } from '@vueuse/core';
import { usePromotion } from '@/helpers/composables/usePromotion';
import { trackFreeTrialSwiperExp } from '../experiments/FreeTrialSwiperExp/tracking';
import { ClickoutPlacement } from '@/interfaces/snowplow/clickout-context';

import { useInternationalOffers } from '../experiments/InternationalOffers/composables/useInternationalOffers';
import { useAppleAmzChannelExp } from '../experiments/AppleAmzChannelExp';

import { useFewerPromotedTagsExpVariant } from '@/components/experiments/FewerPromotedTagsExp';

export default /*#__PURE__*/_defineComponent({
  __name: 'BuyBoxNew',
  props: {
    title: null,
    titleId: null,
    titleName: null,
    titleObjectId: null,
    titleObjectType: null,
    originalReleaseYear: null,
    isTitleUpcoming: { type: Boolean },
    buyboxOffers: { default: undefined },
    sponsoredAd: { default: undefined },
    isTitleInWatchlist: { type: Boolean, default: true },
    buyBoxSwitchType: null,
    hideBuyBoxSwitcher: { type: Boolean, default: false },
    isAVODActive: { type: Boolean, default: false },
    additionalContexts: { default: () => [] },
    seasonNumber: null,
    episodeNumber: null,
    placement: { default: 'regular_buybox' }
  },
  emits: {
	onWatchlistToggle: (createdAt: string | null) => ({ createdAt }),
},
  setup(__props: any, { emit: emits }) {

const props = __props as { title: TitleDetail, titleId: string, titleName?: string, titleObjectId: number, titleObjectType: ObjectType, originalReleaseYear?: number, isTitleUpcoming: boolean, buyboxOffers: BuyBoxOffers | null, sponsoredAd: SponsoredAdFragment | undefined, isTitleInWatchlist: boolean, buyBoxSwitchType?: NewBuyBoxView, hideBuyBoxSwitcher: boolean, isAVODActive: boolean, additionalContexts: SnowplowContext[], seasonNumber?: number | null, episodeNumber?: number | null, placement: ClickoutPlacement };

const OfferReporter = () => import('@/components/buybox/OfferReporter.vue');
const PlexPlayerModal = () => import('@/ui-components/PlexPlayerModal.vue');

const BuyboxInternationalOffers = () =>
	import('../experiments/InternationalOffers/components/BuyboxInternationalOffers.vue');




const { setInWatchlist, onIsLoggedInChange } = useTitleWatchlist(props.sponsoredAd);
const { setNewBuyboxFilterType, newBuyboxFilterType, isLoggedIn } = useUserStore();
const { country, language } = useLanguageStore();
const { titleProviderCount } = useConstantStore();
const { t } = useI18n();
useProvideClickoutPlacement(props.placement);

const isMounted = useMounted();

// FEWER_PROMOTED_TAGS_EXP
const { variant: fewerPromotedTagsExpVariant } = useFewerPromotedTagsExpVariant(
	props.titleObjectType,
	props.title.offers
);
const fewerPromotionExpHidden = computed(() => {
	return fewerPromotedTagsExpVariant.value === 'variant_2';
});
// FEWER_PROMOTED_TAGS_EXP

// APPLE_CHANNELS_EXP
const { appleAmzChannelExpVariant } = useAppleAmzChannelExp(() => props.title);

// APPLE_CHANNELS_EXP

const buyboxContainer = ref<HTMLElement | null>(null);

// episode buybox filter is not currently being stored. keep state for each Episode buybox at buybox level
const episodeBuyBoxType = ref<BuyBoxFilterOptions>(BuyBoxFilterOptions.ALL);

const queryVariables = computed<GetBuyBoxOffersQueryVariables>(() => {
	return {
		nodeId: props.titleId,
		country: country.value,
		language: language.value,
	};
});

const { result, loading } = useQuery<GetBuyBoxOffersQuery, GetBuyBoxOffersQueryVariables>(
	GetBuyBoxOffersDocument,
	queryVariables,
	() => ({ enabled: !props.buyboxOffers })
);

const buyboxData = computed(() => (!props.buyboxOffers ? (result.value?.node as BuyBoxOffers) : props.buyboxOffers));

const isEpisodeBuybox = computed(() => buyboxData.value?.__typename === 'Episode');
const buyboxFilter = computed(() => (isEpisodeBuybox.value ? episodeBuyBoxType.value : newBuyboxFilterType.value));

const buyBoxFilterOptions = computed(() => {
	return Object.keys(buyboxFilterByMonetizationType).filter(monetizationType =>
		allRegularOffers.value.some(offer =>
			buyboxFilterByMonetizationType[monetizationType as keyof typeof buyboxFilterByMonetizationType].includes(
				offer.monetizationType
			)
		)
	) as BuyBoxFilterOptions[];
});

const titleContext = computed(() => {
	const title = props.title.__typename === 'Season' ? props.title.show : props.title;
	return new SnowplowTitleContextGraphql(
		title.objectId,
		title.objectType,
		props.seasonNumber ?? null,
		props.episodeNumber ?? null
	);
});

provide('titleContext', titleContext);
provide('titleSourceId', props.titleId);
provide('filterOption', buyboxFilter);

const { filteredRegularOffers, allRegularOffers, upcomingReleases, upcomingReleasesFromGroups, promotedOffers } =
	useBuyBoxData({
		isAVODActive: () => props.isAVODActive,
		title: () => props.title,
		buyboxData,
		buyboxFilter,
	});

// APPLE_AMZ_EXP
const promotedOffersFiltered = computed(() => {
	if (appleAmzChannelExpVariant.value && !['variant_1', 'variant_3'].includes(appleAmzChannelExpVariant.value))
		return promotedOffers.value.filter(offer => offer.package.shortName !== 'atp');
	return promotedOffers.value;
});
// APPLE_AMZ_EXP

const lastUpdated = computed(() => {
	if (
		buyboxData.value?.__typename === 'Episode' ||
		buyboxData.value?.__typename === 'Movie' ||
		buyboxData.value?.__typename === 'Season' ||
		buyboxData.value?.__typename === 'Show'
	) {
		return buyboxData.value.maxOfferUpdatedAt;
	}
	return null;
});

const timestampText = computed(() => {
	const updated = timestamp(
		lastUpdated.value,
		{ year: 'numeric', month: 'long', day: 'numeric' },
		country.value,
		language.value
	);
	return t('WEBAPP_BUYBOX_UPDATED_TIMESTAMP', {
		services_total: titleProviderCount.value,
		date: updated.date,
		time: updated.time,
	});
});

async function onNotifyMe() {
	if (props.title && props.titleId) {
		const title = props.title.__typename === 'Season' ? props.title.show : props.title;

		const updatedTitle = await setInWatchlist(
			title.id,
			props.isTitleInWatchlist,
			{
				title: title.content.title,
				objectType: title.objectType,
				objectId: title.objectId,
			},
			{
				source: 'buybox',
			}
		);

		if (updatedTitle) {
			emits('onWatchlistToggle', updatedTitle.data?.setInWatchlistV2.title.watchlistEntryV2?.createdAt);
		}
	}

	trackInternationalOffers({
		action: 'click',
		label: `notify_me_clicked`,
	});
}

watch(
	isLoggedIn,
	newValue => {
		if (!newValue) return;
		if (props.title && props.titleId) {
			onIsLoggedInChange(
				props.titleId || '',
				{
					title: props.title?.content.title,
					objectType: props.title?.objectType,
					objectId: props.title?.objectId,
				},
				props.isTitleInWatchlist,
				'buybox'
			);
		}
	},
	{ immediate: true }
);

//

const showInternationalOffers = computed(() => {
	return (
		!loading.value &&
		isMounted.value &&
		allRegularOffers.value.length === 0 &&
		!props.isTitleUpcoming &&
		props.title.__typename === 'Movie'
	);
});

const { expActiveVariant, trackInternationalOffers } = useInternationalOffers(showInternationalOffers);

watch(
	() => expActiveVariant.value,
	() => {
		trackInternationalOffers({
			action: 'impression',
		});
	},
	{ immediate: true }
);

function handleFilterClick(payload: { value: BuyBoxFilterOptions; nonInteraction?: boolean }) {
	isEpisodeBuybox.value ? (episodeBuyBoxType.value = payload.value) : setNewBuyboxFilterType(payload.value);

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'filter_toggled',
			label: 'buybox',
			property: payload.value,
			nonInteraction: payload.nonInteraction,
		},
		props.additionalContexts
	);

	// FT_SWIPER_EXP
	if (!payload.nonInteraction && !inPromotionCatalogue.value) {
		trackFreeTrialSwiperExp({
			action: 'click',
			label: `filter_toggled_${payload.value}`,
		});
	}
	// FT_SWIPER_EXP

	trackInternationalOffers({
		action: 'click',
		label: `filter_${payload.value}`,
	});
}

function watchNowTitleClicked() {
	TrackingHelper.trackEvent(
		'userinteraction',
		{ action: 'click', label: 'watch_now_headline_clicked' },
		props.additionalContexts
	);

	if (!props.isAVODActive) {
		const element = buyboxContainer.value?.querySelector('#regular-buybox');
		if (element) {
			element.scrollIntoView({ behavior: 'smooth' });
		}
	} else {
		ModalHelper.openModal(
			PlexPlayerModal,
			{ offer: props.title?.plexPlayerOffers[0], title: props.title },
			{ cssClass: 'trailer-modal' },
			'',
			true,
			[]
		);
	}
}

// FT_SWIPER_EXP
const swipeIndex = ref<number | null>(null);
const setSwiperIndex = (index: number) => {
	swipeIndex.value = index;
};
provide('swipeIndex', swipeIndex);
provide('setSwiperIndex', setSwiperIndex);

const { inPromotionCatalogue } = usePromotion({ title: props.title, offers: allRegularOffers });

onMounted(async () => {
	await until(result).toBeTruthy();
	if (!inPromotionCatalogue.value) {
		trackFreeTrialSwiperExp({
			action: 'impression',
		});
	}
});
// FT_SWIPER_EXP

return { __sfc: true,OfferReporter, PlexPlayerModal, BuyboxInternationalOffers, emits, props, setInWatchlist, onIsLoggedInChange, setNewBuyboxFilterType, newBuyboxFilterType, isLoggedIn, country, language, titleProviderCount, t, isMounted, fewerPromotedTagsExpVariant, fewerPromotionExpHidden, appleAmzChannelExpVariant, buyboxContainer, episodeBuyBoxType, queryVariables, result, loading, buyboxData, isEpisodeBuybox, buyboxFilter, buyBoxFilterOptions, titleContext, filteredRegularOffers, allRegularOffers, upcomingReleases, upcomingReleasesFromGroups, promotedOffers, promotedOffersFiltered, lastUpdated, timestampText, onNotifyMe, showInternationalOffers, expActiveVariant, trackInternationalOffers, handleFilterClick, watchNowTitleClicked, swipeIndex, setSwiperIndex, inPromotionCatalogue, RegularBuyBoxNew, BuyBoxCountrySwitcher, PromotedBuyBoxNew, BuyBoxFilterNew, BuyBoxTypeSwitcher, BuyBoxUpcomingWithNoOffers }
}

})